// Number, date and HTML helpers (Australian formatting).
export const esc = (s: unknown) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

// Yahoo sends { raw, fmt }; ASX sometimes sends numeric strings.
export function n(v: unknown): number | null {
  if (v && typeof v === "object" && "raw" in v) v = (v as { raw: unknown }).raw;
  if (typeof v === "string" && v.trim() !== "") v = Number(v.replace(/[,%$]/g, ""));
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

const DASH = "–";

export function price(v: number | null | undefined, currency = "AUD") {
  if (v == null) return DASH;
  const digits = Math.abs(v) < 1 ? 3 : 2;
  return new Intl.NumberFormat("en-AU", { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
}

export function money(v: number | null | undefined, currency = "AUD") {
  if (v == null) return DASH;
  return new Intl.NumberFormat("en-AU", { style: "currency", currency, notation: "compact", maximumFractionDigits: 2 }).format(v);
}

export function compact(v: number | null | undefined) {
  if (v == null) return DASH;
  return new Intl.NumberFormat("en-AU", { notation: "compact", maximumFractionDigits: Math.abs(v) >= 1e9 ? 1 : 2 }).format(v);
}

export function fixed(v: number | null | undefined, digits = 2) {
  if (v == null) return DASH;
  return v.toLocaleString("en-AU", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

// v is a fraction (0.05 = 5%).
export function pct(v: number | null | undefined, signed = false) {
  if (v == null) return DASH;
  const s = (v * 100).toLocaleString("en-AU", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
  return signed && v > 0 ? "+" + s : s;
}

export function date(v: number | string | null | undefined) {
  if (v == null) return DASH;
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

export function dateTime(v: number | string | null | undefined) {
  if (v == null) return DASH;
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Australia/Sydney" });
}

// "priceEarningsRatio" → "Price earnings ratio"
export function humanize(key: string) {
  const s = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const tone = (v: number | null | undefined) => (v == null || v === 0 ? "" : v > 0 ? "up" : "down");

// Sydney date as YYYY-MM-DD (ASX trading days).
export const sydneyDate = (d: Date | number = new Date()) =>
  new Date(typeof d === "number" ? d * 1000 : d).toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });

// "today", or "on 20 Oct" when the latest price is from an earlier trading day.
export function dayLabel(priceDate: string | null | undefined) {
  if (!priceDate || priceDate === sydneyDate()) return "today";
  return "on " + new Date(`${priceDate}T12:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

// Stock codes link to their stock page everywhere, as a pill with marks: ● held, ★ watched,
// ⊘ blacklisted (greyed and struck through). The marks come from /api/marks (setMarks, kept fresh
// by app.ts). Long-press or right-click a code for a quick menu (stockmenu.ts). Use `stockAttr` on
// an element that isn't a link (a chip or pick inside a <summary>): app.ts opens #/CODE on click.
export interface Marks { held: string[]; watched: string[]; lists: Record<string, string[]>; blacklisted: Record<string, { reason: string | null; addedAt: string }> }
let marks: Marks = { held: [], watched: [], lists: {}, blacklisted: {} };
export const setMarks = (m: Marks) => { marks = m; };
export const getMarks = () => marks;
export function markIcons(code: string) {
  const out: string[] = [];
  if (marks.held.includes(code)) out.push(`<i class="mk held" title="In your portfolio">●</i>`);
  if (marks.watched.includes(code)) out.push(`<i class="mk watched" title="On your watchlist${marks.lists[code]?.length ? `: ${esc(marks.lists[code].join(", "))}` : ""}">★</i>`);
  const b = marks.blacklisted[code];
  if (b) out.push(`<i class="mk black" title="Blacklisted${b.reason ? `: ${esc(b.reason)}` : ""}">⊘</i>`);
  return out.join("");
}
const pillClass = (code: string) => `code-pill${marks.blacklisted[code] ? " is-black" : ""}`;
export const stockAttr = (code: string) => ` data-stock="${esc(code)}" data-code="${esc(code)}" role="link" tabindex="0" title="Open ${esc(code)}"`;
/** A stock code as a pill linking to its page, with its marks. (`_inner` is ignored: codes always look the same.) */
export const stockLink = (code: string, _inner?: string) => `<a class="${pillClass(code)}" href="#/${esc(code)}" data-code="${esc(code)}"><b>${esc(code)}</b>${markIcons(code)}</a>`;
/** The same pill for a code shown inside another element (e.g. a chip or a <summary>), not as a link of its own. */
export const codePill = (code: string) => `<span class="${pillClass(code)}"><b>${esc(code)}</b>${markIcons(code)}</span>`;

/** Turn mentions of the given codes in already-escaped HTML into stock links (outside existing links and tags). */
export function linkCodes(html: string, codes: Iterable<string>): string {
  const list = [...new Set([...codes].filter((c) => /^[A-Z0-9]{3,6}$/.test(c)))];
  if (!list.length) return html;
  const re = new RegExp(`\\b(${list.join("|")})\\b`, "g");
  let inLink = 0;
  return html.split(/(<[^>]+>)/).map((part) => {
    if (part.startsWith("<")) {
      if (/^<a[\s>]/i.test(part)) inLink++;
      else if (/^<\/a>/i.test(part)) inLink = Math.max(0, inLink - 1);
      return part;
    }
    return inLink ? part : part.replace(re, (c) => stockLink(c));
  }).join("");
}
